import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import {
  createMathRecords,
  downloadMathDocument,
  fetchMathColumns,
  fetchMathRows,
  mathErrorMessage,
  recalculateMath,
  reindexMathRecords,
  saveMathCell,
  saveMathStatus
} from './mathReportApi';
import type { MathColumn, MathPeriod, MathRow } from './mathReportApi';
import { getCurrentMonth, parseMonth } from './mathReportCalc';

export type MathBusyAction = 'create' | 'reindex' | 'recalculate';

function patchRow(rows: MathRow[] | null, id: number, patch: Partial<MathRow>): MathRow[] | null {
  // Меняется только одна запись, остальные сохраняют ссылки: строки таблицы в memo не перерисовываются
  return rows && rows.map((row) => (row.id === id ? { ...row, ...patch } : row));
}

/** Данные вкладки «Мат. отчет → Отчет»: загрузка месяца, правки ячеек и массовые действия */
export function useMathReport() {
  const [month, setMonth] = useState(getCurrentMonth);
  const [columns, setColumns] = useState<MathColumn[] | null>(null);
  const [rows, setRows] = useState<MathRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<MathBusyAction | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const columnsRef = useRef<MathColumn[] | null>(null);
  const requestRef = useRef(0);
  const busyRef = useRef(false);
  const downloadingRef = useRef(false);
  const togglingRef = useRef<Record<number, boolean>>({});
  // Сохранения ячеек, которые еще идут: массовое действие дожидается их, чтобы не обогнать правку
  const pendingRef = useRef<Promise<void>[]>([]);

  const trackPending = useCallback((task: Promise<unknown>) => {
    const settled: Promise<void> = task.then(
      () => undefined,
      () => undefined
    );
    pendingRef.current.push(settled);
    settled.then(() => {
      pendingRef.current = pendingRef.current.filter((item) => item !== settled);
    });
  }, []);

  /** keepRows: после действия на экране остаются прежние строки (приглушенные), а не пустой экран */
  const load = useCallback(
    async (keepRows: boolean) => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      setLoading(true);
      setError('');
      if (!keepRows) setRows(null);

      try {
        const [list, loadedColumns] = await Promise.all([
          fetchMathRows(parseMonth(month)),
          columnsRef.current ? Promise.resolve(columnsRef.current) : fetchMathColumns()
        ]);
        // Устаревший ответ (месяц уже сменили или экран закрыт) отбрасывается
        if (requestId !== requestRef.current) return;
        columnsRef.current = loadedColumns;
        setColumns(loadedColumns);
        setRows(list);
      } catch (err) {
        if (requestId !== requestRef.current) return;
        const reason = mathErrorMessage(err, 'Проверьте соединение и повторите попытку');
        if (keepRows) notify(`Не удалось обновить мат. отчет. ${reason}`, 'error');
        else setError(reason);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    },
    [month]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  const saveCell = useCallback(
    async (id: number, field: string, value: number): Promise<boolean> => {
      if (busyRef.current) return false;

      const task = saveMathCell(id, field, value);
      trackPending(task);
      try {
        await task;
        setRows((prev) => patchRow(prev, id, { [field]: value }));
        notify('Значение успешно обновлено', 'success');
        return true;
      } catch (err) {
        notify(mathErrorMessage(err, 'Ошибка при обновлении значения'), 'error');
        return false;
      }
    },
    [trackPending]
  );

  const toggleStatus = useCallback(
    async (id: number, current: number) => {
      // Повторный щелчок по строке, пока прежний запрос идет, игнорируется
      if (busyRef.current || togglingRef.current[id]) return;
      togglingRef.current[id] = true;

      const next = current === 1 ? 0 : 1;
      const task = saveMathStatus(id, next);
      trackPending(task);
      try {
        await task;
        setRows((prev) => patchRow(prev, id, { checkStatus: next }));
        notify(`Статус успешно ${next === 1 ? 'установлен' : 'снят'}`, 'success');
      } catch (err) {
        notify(mathErrorMessage(err, 'Ошибка при обновлении статуса'), 'error');
      } finally {
        delete togglingRef.current[id];
      }
    },
    [trackPending]
  );

  const download = useCallback(
    async (row: MathRow) => {
      if (busyRef.current || downloadingRef.current) return;
      downloadingRef.current = true;
      setDownloadingId(row.id);
      try {
        await downloadMathDocument(row, month);
        notify('Отчет успешно сформирован', 'success');
      } catch (err) {
        notify(mathErrorMessage(err, 'Ошибка при формировании отчета'), 'error');
      } finally {
        downloadingRef.current = false;
        setDownloadingId(null);
      }
    },
    [month]
  );

  /** Массовое действие: экран блокируется до конца действия и последующей перезагрузки месяца */
  const runAction = useCallback(
    async (action: MathBusyAction, request: (period: MathPeriod) => Promise<string>, failText: string, reloadOnFail: boolean) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(action);

      let done = false;
      try {
        await Promise.all(pendingRef.current);
        notify(await request(parseMonth(month)), 'success');
        done = true;
      } catch (err) {
        notify(mathErrorMessage(err, failText), 'error');
      }

      try {
        if (done || reloadOnFail) await load(true);
      } finally {
        busyRef.current = false;
        setBusy(null);
      }
    },
    [load, month]
  );

  const createRecords = useCallback(
    () => runAction('create', createMathRecords, 'Ошибка при создании записей', false),
    [runAction]
  );

  const reindex = useCallback(
    () => runAction('reindex', reindexMathRecords, 'Ошибка при перепроверке', false),
    [runAction]
  );

  // После сбоя пересчет мог пройти частично, поэтому данные перезагружаются в любом случае
  const recalculate = useCallback(
    () =>
      runAction(
        'recalculate',
        async (period) => `Пересчет выполнен успешно. Обновлено месяцев: ${await recalculateMath(period)}`,
        'Ошибка при пересчете',
        true
      ),
    [runAction]
  );

  return {
    month,
    setMonth,
    columns,
    rows,
    loading,
    error,
    retry,
    busy,
    downloadingId,
    saveCell,
    toggleStatus,
    download,
    createRecords,
    reindex,
    recalculate
  };
}
